import React, { useState, useEffect, useCallback } from 'react';
import { Shield, ShieldAlert, Search, X, Check, AlertCircle, Loader2, Save, User, LayoutDashboard, Activity, Ticket, DollarSign, Users, HeartPulse } from 'lucide-react';
import authService from '../services/auth';
import { ALL_MENUS } from '../context/AuthContext';

const ACTIONS = ['active', 'add', 'edit', 'view', 'delete', 'print'];

const ACTION_LABELS = {
  active: 'Aktif', add: 'Tambah', edit: 'Edit', view: 'Lihat', delete: 'Hapus', print: 'Cetak'
};

const ROLES = [
  { value: 'admin', label: 'Admin', color: 'from-red-500 to-rose-600' },
  { value: 'user', label: 'User', color: 'from-blue-500 to-indigo-600' },
  { value: 'viewer', label: 'Viewer', color: 'from-green-500 to-emerald-600' },
  { value: 'perawat', label: 'Perawat', color: 'from-purple-500 to-pink-600' },
];

const MENU_GROUPS = [
  { key: 'Dashboard', icon: LayoutDashboard, color: 'from-violet-500 to-purple-600', label: 'Dashboard' },
  { key: 'Activity', icon: Activity, color: 'from-blue-500 to-indigo-600', label: 'Activity' },
  { key: 'Ticketing', icon: Ticket, color: 'from-emerald-500 to-teal-600', label: 'Ticketing' },
  { key: 'Keuangan', icon: DollarSign, color: 'from-amber-500 to-orange-600', label: 'Keuangan' },
  { key: 'User', icon: Users, color: 'from-rose-500 to-pink-600', label: 'User' },
  { key: 'Antrian', icon: HeartPulse, color: 'from-red-500 to-pink-600', label: 'Antrian Klinik' },
];

const HakAkses = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await authService.getUsers();
      setUsers(data.users || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const selectUser = (user) => {
    setSelectedUser(user);
    // Parse permissions - if admin, set all true
    if (user.role === 'admin') {
      const allPerms = {};
      ALL_MENUS.forEach(m => {
        allPerms[m.id] = { active: true, add: true, edit: true, view: true, delete: true, print: true };
      });
      setPermissions(allPerms);
    } else if (user.permissions) {
      setPermissions(typeof user.permissions === 'string' ? JSON.parse(user.permissions) : user.permissions);
    } else {
      const defaultPerms = {};
      ALL_MENUS.forEach(m => {
        defaultPerms[m.id] = { active: true, add: false, edit: false, view: true, delete: false, print: false };
      });
      setPermissions(defaultPerms);
    }
    setSuccessMessage('');
  };

  const togglePermission = (menuId, action) => {
    setPermissions(prev => ({
      ...prev,
      [menuId]: {
        ...(prev[menuId] || {}),
        [action]: !(prev[menuId]?.[action] ?? false)
      }
    }));
  };

  const setAllPermissions = (value) => {
    const newPerms = {};
    ALL_MENUS.forEach(m => {
      newPerms[m.id] = { active: value, add: value, edit: value, view: value, delete: value, print: value };
    });
    setPermissions(newPerms);
  };

  const handleSave = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await authService.updateUser(selectedUser._id, { permissions, role: selectedUser.role });
      setSelectedUser(prev => ({ ...prev, permissions }));
      setSuccessMessage('Hak akses berhasil disimpan');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = users.filter(u =>
    !searchTerm || u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.name && u.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Hak Akses</h2>
          <p className="mt-1 text-sm text-gray-500">Manage user permissions for each menu</p>
        </div>
      </div>

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 animate-slide-up">
          <div className="flex items-center space-x-2"><Check className="w-5 h-5 text-green-500" /><p className="text-sm font-medium text-green-800">{successMessage}</p></div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User List */}
        <div className="lg:col-span-1">
          <div className="card">
            <div className="px-4 py-3 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input type="text" placeholder="Search user..." value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-gray-50 focus:bg-white text-sm" />
              </div>
            </div>
            <div className="max-h-[500px] overflow-y-auto">
              {filteredUsers.map(user => (
                <button key={user._id} onClick={() => selectUser(user)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left border-b border-gray-50 last:border-0 ${selectedUser?._id === user._id ? 'bg-blue-50' : ''}`}>
                  <div className={`flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br ${ROLES.find(r => r.value === user.role)?.color || 'from-gray-500 to-gray-600'} flex items-center justify-center`}>
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900 truncate">{user.name || user.username}</p>
                    <p className="text-xs text-gray-400">@{user.username} • {user.bagian || 'Umum'}</p>
                  </div>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                    user.role === 'admin' ? 'bg-red-100 text-red-700' :
                    user.role === 'user' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                  }`}>{user.role}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Permission Editor */}
        <div className="lg:col-span-2">
          {selectedUser ? (
            <div className="card">
              <div className="px-6 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">
                      Permissions: {selectedUser.name || selectedUser.username}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {selectedUser.role === 'admin' ? 'Admin has full access to all menus' : `Configure access for each menu`}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {selectedUser.role !== 'admin' && (
                      <>
                        <button onClick={() => setAllPermissions(true)} className="btn-secondary text-xs"><Check className="w-3.5 h-3.5" /><span>All ON</span></button>
                        <button onClick={() => setAllPermissions(false)} className="btn-secondary text-xs"><X className="w-3.5 h-3.5" /><span>All OFF</span></button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {selectedUser.role === 'admin' ? (
                <div className="px-6 py-12 text-center">
                  <ShieldAlert className="w-16 h-16 text-red-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg font-medium">Admin has full access</p>
                  <p className="text-gray-400 text-sm mt-1">All menus and actions are enabled for admin users</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-100">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Menu</th>
                          {ACTIONS.map(action => (
                            <th key={action} className="px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase">{ACTION_LABELS[action]}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {MENU_GROUPS.map((group, gi) => {
                          const groupMenus = ALL_MENUS.filter(m => m.group === group.key);
                          if (groupMenus.length === 0) return null;
                          return (
                            <React.Fragment key={group.key}>
                              {/* Group Header */}
                              {gi > 0 && <tr className="h-2" />}
                              <tr className="bg-gray-50">
                                <td colSpan={7} className="px-4 py-3">
                                  <div className="flex items-center space-x-2.5">
                                    <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${group.color} flex items-center justify-center shadow-sm`}>
                                      <group.icon className="w-3.5 h-3.5 text-white" />
                                    </div>
                                    <span className="text-sm font-bold text-gray-700 uppercase tracking-wider">{group.label}</span>
                                  </div>
                                </td>
                              </tr>
                              {/* Menu Items */}
                              {groupMenus.map(menu => {
                                const menuPerms = permissions[menu.id] || {};
                                return (
                                  <tr key={menu.id} className="hover:bg-gray-50 transition-colors border-t border-gray-50">
                                    <td className="px-4 py-2.5 text-sm font-medium text-gray-700 pl-10">
                                      <span className="flex items-center space-x-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                                        <span>{menu.label}</span>
                                      </span>
                                    </td>
                                    {ACTIONS.map(action => {
                                      const isActive = menuPerms[action] === true;
                                      return (
                                        <td key={action} className="px-3 py-2.5 text-center">
                                          <button
                                            onClick={() => togglePermission(menu.id, action)}
                                            className={`w-7 h-7 rounded-lg transition-all flex items-center justify-center mx-auto ${
                                              isActive
                                                ? 'bg-green-100 text-green-600 hover:bg-green-200'
                                                : 'bg-gray-100 text-gray-300 hover:bg-gray-200'
                                            }`}
                                            title={`${isActive ? 'Disable' : 'Enable'} ${ACTION_LABELS[action]} for ${menu.label}`}
                                          >
                                            {isActive ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                                          </button>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="px-6 py-4 border-t border-gray-100 flex justify-between items-center">
                    <p className="text-xs text-gray-400">
                      {Object.values(permissions).filter(p => p.active).length}/{ALL_MENUS.length} menus active
                    </p>
                    <button onClick={handleSave} disabled={saving} className="btn-primary text-sm">
                      {saving ? <><Loader2 className="w-4 h-4 animate-spin" /><span>Saving...</span></>
                        : <><Save className="w-4 h-4" /><span>Save Permissions</span></>}
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="card p-12 text-center">
              <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg font-medium">Select a User</p>
              <p className="text-gray-400 text-sm mt-1">Choose a user from the list to manage their access rights</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HakAkses;
